// ============================================================================
//  imports.api.ts — Couche API Import/Export
//  [F7] Zone staging + validation 4 yeux — compliance-senegal.md §9
// ============================================================================

import api from '@/lib/axios'

// ─── Types ───────────────────────────────────────────────────────────────────

export type ImportModule = 'accounting' | 'payroll' | 'hr' | 'crm' | 'projects' | 'treasury'
export type ImportType   =
  | 'JOURNAL_ENTRIES' | 'CHART_OF_ACCOUNTS'
  | 'EMPLOYEES' | 'PAYROLL_VARIABLES'
  | 'CRM_CONTACTS' | 'CRM_COMPANIES' | 'CRM_OPPORTUNITIES'
  | 'TASKS' | 'BANK_TRANSACTIONS'

export type ImportStatus = 'STAGING' | 'VALIDATING' | 'APPROVED' | 'REJECTED' | 'INTEGRATED' | 'PARTIAL'

export interface ImportBatch {
  id:                   string
  module:               ImportModule
  type:                 ImportType
  status:               ImportStatus
  fileName:             string
  fileUrl:              string
  fileHash:             string
  totalRows:            number
  validRows:            number
  errorRows:            number
  requiresDualValidation: boolean
  firstValidatorId?:    string
  firstValidatedAt?:    string
  secondValidatorId?:   string
  secondValidatedAt?:   string
  rejectedById?:        string
  rejectedAt?:          string
  rejectionReason?:     string
  integratedAt?:        string
  integratedById?:      string
  errorReport?:         ImportErrorReport
  createdById:          string
  createdAt:            string
  lines?:               ImportBatchLine[]
}

export interface ImportBatchLine {
  id:           string
  rowNumber:    number
  rawData:      Record<string, any>
  parsedData?:  Record<string, any>
  isValid:      boolean
  errors?:      string[]
  isIntegrated: boolean
  integratedAt?: string
  resourceId?:  string
}

export interface ImportErrorReport {
  summary:     { total: number; valid: number; errors: number }
  firstErrors: Array<{ row: number; errors: string[] }>
  integrationErrors?: Array<{ row: number; error: string }>
  /** Avertissements non bloquants de l'upload (ex. exercice en clôture provisoire). */
  warnings?:          string[]
}

export interface ListImportsParams {
  module?:  ImportModule
  status?:  ImportStatus
  page?:    number
  limit?:   number
}

export interface PaginatedImports {
  data: ImportBatch[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

export interface IntegrationResult {
  status:            ImportStatus
  integratedCount:   number
  errorCount:        number
  integrationErrors: Array<{ row: number; error: string }>
}

/** Résultat de la validation en masse des écritures créées par un lot JOURNAL_ENTRIES. */
export interface ValidateEntriesResult {
  total:            number
  validated:        number
  alreadyValidated: number
  errors:           Array<{ row: number; reference: string | null; error: string }>
}

// ─── Labels UI ───────────────────────────────────────────────────────────────

export const MODULE_LABELS: Record<ImportModule, string> = {
  accounting: 'Comptabilité',
  payroll:    'Paie',
  hr:         'Ressources Humaines',
  crm:        'CRM',
  projects:   'Projets',
  treasury:   'Trésorerie',
}

export const TYPE_LABELS: Record<ImportType, string> = {
  JOURNAL_ENTRIES:   'Écritures comptables',
  CHART_OF_ACCOUNTS: 'Plan comptable',
  EMPLOYEES:         'Employés',
  PAYROLL_VARIABLES: 'Éléments variables de paie',
  CRM_CONTACTS:      'Contacts CRM',
  CRM_COMPANIES:     'Entreprises CRM',
  CRM_OPPORTUNITIES: 'Opportunités CRM',
  TASKS:             'Tâches projet',
  BANK_TRANSACTIONS: 'Transactions bancaires',
}

export const REQUIRED_COLUMNS: Record<ImportType, string[]> = {
  JOURNAL_ENTRIES:   ['date', 'journal_code', 'debit_account', 'credit_account', 'amount', 'description'],
  CHART_OF_ACCOUNTS: ['code', 'name', 'type'],
  EMPLOYEES:         ['last_name', 'first_name', 'hire_date', 'contract_type'],
  PAYROLL_VARIABLES: ['employee_id', 'period', 'element_type', 'amount'],
  CRM_CONTACTS:      ['last_name', 'first_name', 'email'],
  CRM_COMPANIES:     ['name'],
  CRM_OPPORTUNITIES: ['name', 'amount', 'stage'],
  TASKS:             ['title', 'project_key'],
  BANK_TRANSACTIONS: ['date', 'amount', 'description'],
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const importsApi = {
  list: (params?: ListImportsParams): Promise<PaginatedImports> =>
    api.get('/imports', { params }).then(r => r.data.data ?? r.data),

  getOne: (id: string): Promise<ImportBatch> =>
    api.get(`/imports/${id}`).then(r => r.data.data ?? r.data),

  getLines: (id: string, onlyErrors?: boolean): Promise<ImportBatchLine[]> =>
    api.get(`/imports/${id}/lines`, { params: { onlyErrors } }).then(r => r.data.data ?? r.data),

  upload: (file: File, module: ImportModule, type: ImportType): Promise<ImportBatch> => {
    const form = new FormData()
    form.append('file',   file)
    form.append('module', module)
    form.append('type',   type)
    return api.post('/imports/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data.data ?? r.data)
  },

  validate: (id: string): Promise<ImportBatch> =>
    api.post(`/imports/${id}/validate`).then(r => r.data.data ?? r.data),

  reject: (id: string, rejectionReason: string): Promise<ImportBatch> =>
    api.post(`/imports/${id}/reject`, { rejectionReason }).then(r => r.data.data ?? r.data),

  // Timeout étendu (5 min) : l'intégration d'un gros lot (ex. 417 écritures) dépasse les 30 s par défaut.
  integrate: (id: string): Promise<IntegrationResult> =>
    api.post(`/imports/${id}/integrate`, undefined, { timeout: 300_000 }).then(r => r.data.data ?? r.data),

  /** Valide (brouillon → validée) les écritures créées par un lot intégré. Permission : accounting:validate_entry. */
  validateEntries: (id: string): Promise<ValidateEntriesResult> =>
    api.post(`/imports/${id}/validate-entries`, undefined, { timeout: 300_000 }).then(r => r.data.data ?? r.data),
}
